import type { ReactNode } from "react";

/**
 * 테이블 — 원본 `app.css` 의 `.table-scroll` + `table`/`th`/`td` 규칙.
 *
 * `min-w-[640px]` + 바깥 `overflow-x-auto` 로 모바일에서 가로 스크롤시킵니다. 열을 숨기거나
 * 카드로 바꾸지 않는 이유는 이 테이블들이 **관리 화면**이고, 어떤 열이 판단에 필요한지는
 * 운영자마다 달라서 임의로 감추면 작업이 막히기 때문입니다.
 *
 * `tbody tr:last-child td { border-bottom: none }` 과 `tbody tr:hover` 는 Tailwind 의 임의 변형
 * (`[&>tr:last-child>td]:…`)으로 옮겼습니다 — 자식 선택자 하나를 위해 전역 CSS 를 늘리지 않습니다.
 */

export function TableScroll({ children }: { children: ReactNode }) {
  return <div className="overflow-x-auto">{children}</div>;
}

export function Table({ children }: { children: ReactNode }) {
  // body 의 `overflow-wrap: anywhere` 는 셀의 최소폭을 글자 1개로 줄여 열이 글자 단위로 쪼개집니다.
  // `break-word` 는 최소폭에 영향이 없어 긴 값은 열을 넓히고, 넘치는 분은 바깥 가로 스크롤이 받습니다.
  return (
    <table className="w-full min-w-[640px] border-collapse [overflow-wrap:break-word]">
      {children}
    </table>
  );
}

export function Thead({ columns }: { columns: string[] }) {
  return (
    <thead>
      <tr>
        {columns.map((c, i) => (
          <th
            // 빈 제목(액션 열)이 두 개 이상일 수 있어 인덱스를 키에 섞습니다.
            key={`${c}-${String(i)}`}
            className="whitespace-nowrap border-b border-grey-100 px-[20px] py-[12px] text-left text-[12px] font-bold text-grey-500"
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function Tbody({ children }: { children: ReactNode }) {
  return (
    <tbody className="[&>tr:hover]:bg-grey-050 [&>tr:last-child>td]:border-b-0">{children}</tbody>
  );
}

export function Td({ children }: { children: ReactNode }) {
  return (
    <td className="border-b border-grey-050 px-[20px] py-[12px] text-left align-middle text-[14px]">
      {children}
    </td>
  );
}

/** 셀 안의 강조 줄 / 보조 줄 / 액션 묶음 — 원본 `.cell-strong` / `.cell-muted` / `.cell-actions`. */
export const CELL_STRONG = "font-semibold";
export const CELL_MUTED = "text-[12px] text-grey-400";
export const CELL_ACTIONS = "flex justify-end gap-[6px]";
export const MONO = "font-mono text-[12px]";
